import { useQuery } from '@tanstack/react-query';
import { apiFetch, unwrapData } from '../../utils/api';
import { useApp } from './useApp';

/**
 * Cuenta las empresas accesibles por el usuario autenticado, CROSS-TENANT.
 * Lo usan el sidebar y `useCanSeePortalAsesor` (caso b: owner/admin
 * multi-empresa) para decidir si mostrar el Portal del Asesor.
 *
 * ⚠️ USA `GET /api/auth/companies` (cross-tenant: filtra por user_id, excluye
 * archived/deleted, devuelve `{ data: [...] }`). NO usar `/api/user-companies`,
 * que es un CRUD tenant-scoped (filtra por la company ACTIVA) y por la
 * constraint `@@unique([user_id, company_id])` devuelve SIEMPRE ≤1 fila — lo
 * que rompía en silencio el gate (count nunca > 1). Es el MISMO endpoint que
 * usa el `CompanySwitcher` para pintar su lista, así que la visibilidad del
 * Portal y la del switcher trigger quedan consistentes.
 *
 * Caso histórico del bug: el trigger del switcher (#787 / TopBar) y el caso (b)
 * de `useCanSeePortalAsesor` ambos dependían de este count y no aparecían nunca.
 *
 * Devuelve `0` mientras carga / si el user aún no está disponible — estado
 * neutral; el caller decide cómo tratarlo. staleTime 5 min: el set cambia
 * raramente durante la sesión.
 *
 * ⚠️ Ese `0` es AMBIGUO: no distingue "no tiene empresas" de "todavía no ha
 * respondido". Un caller que decida una NAVEGACIÓN con el count (los gates de
 * `routeHelpers`) no puede usarlo tal cual — vería `0`, tomaría la decisión de
 * la cuenta mono-empresa y la corregiría al resolver, que es el parpadeo
 * ARIA → "Mis empresas". Para eso está `useUserCompaniesCountState`.
 */
export function useUserCompaniesCount(): number {
  return useUserCompaniesCountState().count;
}

/**
 * Igual que `useUserCompaniesCount`, pero distingue "aún no ha respondido" de
 * "ha respondido 0". Lo necesitan los gates de ruta, que con el count a `0`
 * renderizan el destino equivocado y lo corrigen un frame después.
 *
 * `isResolved` es cierto cuando ya hay una decisión que tomar:
 *   - sin `user.id` la query está `enabled: false` y no hay nada que esperar
 *     (quien decide ahí es el `loading` de la sesión, antes que este count);
 *     devolver `false` aquí dejaría al gate esperando para siempre.
 *   - con datos en caché — incluida la que sobrevive al `hardReset` de
 *     `switchCompany`, que preserva a propósito esta clave (ver `useApp`).
 *   - si la query falló: no habrá respuesta, así que se cae al `0` de siempre
 *     en vez de dejar la pantalla en blanco.
 */
export function useUserCompaniesCountState(): { count: number; isResolved: boolean } {
  const { user } = useApp();

  const { data, isError } = useQuery({
    queryKey: ['switchable-companies', user?.id],
    queryFn: async (): Promise<unknown[]> => unwrapData(await apiFetch('/api/auth/companies')),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    count: data?.length ?? 0,
    isResolved: !user?.id || data !== undefined || isError,
  };
}

export default useUserCompaniesCount;
